import { Survey } from '.';
import _isNil from 'lodash/isNil';
import SurveyResponse from './SurveyResponse';
import { FAVORITE_SYSTEM } from '../../SubjectViewer/common/Favorite/util';

export type DisplayQuestionResponseResult = {
  linkId: string;
  type: 'display';
  label: string;
  sameLine: boolean;
  isFavorite: boolean;
};

export type ValueQuestionResponseResult = {
  linkId: string;
  type: 'question';
  label: string;
  value: string;
  sameLine: boolean;
  score?: string;
  isFavorite: boolean;
};

export type QuestionResponseResult =
  | ValueQuestionResponseResult
  | DisplayQuestionResponseResult;

export type SectionResponseResult = {
  linkId: string;
  type: 'section';
  subResults: ResponseResult[];
  label: string;
  sum?: string;
  avg?: string;
};

export type ResponseResult = QuestionResponseResult | SectionResponseResult;

export type SurveyTotalResponseResult = {
  type: 'survey';
  label: string;
  sum?: string;
  avg?: string;
};

export type TotalResult = {
  lineItems: ResponseResult[];
  surveyTotal: SurveyTotalResponseResult | null;
};

export type SurveyResponseResultsConfig = {
  excludeDisplayItems?: boolean;
  excludeNonFavorites?: boolean;
};

/**
 * Utility class for matching a specified survey with a specified response and joining
 * the data in an easy way for the UI to display
 */
class SurveyResponseResults {
  public static isSectionResult(
    responseResult: ResponseResult,
  ): responseResult is SectionResponseResult {
    return responseResult.type === 'section';
  }

  public static isValueQuestionResult(
    responseResult: ResponseResult,
  ): responseResult is ValueQuestionResponseResult {
    return responseResult.type === 'question';
  }

  public static isDisplayQuestionResult(
    responseResult: ResponseResult,
  ): responseResult is DisplayQuestionResponseResult {
    return responseResult.type === 'display';
  }

  private _survey: Survey;
  private _surveyResponse: SurveyResponse;
  private _config: SurveyResponseResultsConfig;

  constructor(
    survey: Survey,
    surveyResponse: SurveyResponse,
    config?: SurveyResponseResultsConfig,
  ) {
    this._survey = survey;
    this._surveyResponse = surveyResponse;
    this._config = config || {};
  }

  /**
   * Are the underlying survey and response valid for listing results
   */
  public get isValid() {
    return !!this._survey && !!this._surveyResponse;
  }

  private _getNumberDisplayValue = (value: number): string =>
    String(parseFloat(value.toFixed(4)));

  private _isFavoriteOrParentOfFavorite = (
    item: fhir.QuestionnaireResponseItem,
  ): boolean => {
    return (
      !!item.extension?.some(e => e.url === FAVORITE_SYSTEM) ||
      !!item.item?.some(this._isFavoriteOrParentOfFavorite)
    );
  };

  /**
   * Individual questions with value responses
   */
  private get _valueQuestionResponseResult(): ValueQuestionResponseResult[] {
    const questionResponseResults = this._surveyResponse.itemsWithValues
      .map(_ => ({
        responseItem: _,
        surveyItem: this._survey.getSurveyItemWithLinkId(_.link),
      }))
      .filter(_ => !!_?.responseItem && !!_?.surveyItem)
      .map<ValueQuestionResponseResult>(_ => {
        return {
          type: 'question',
          linkId: _.surveyItem.link,
          label: _.surveyItem.text,
          value: _.responseItem.valueDisplay,
          sameLine: _.surveyItem.isOnSameLineAsPreviousItem,
          isFavorite: this._isFavoriteOrParentOfFavorite(
            _.responseItem.toFhirResponseItem(),
          ),
          score: this._survey
            .getResponseItemScoringValue(_.responseItem)
            .join(', '),
        };
      });
    return questionResponseResults;
  }

  /**
   * Individual display questions
   */
  private get _displayQuestionDisplayResult(): DisplayQuestionResponseResult[] {
    const displayItems = this._survey.allItems
      .filter(item => {
        if (item.type !== 'display') return false;
        const dependentItemLinks = item.itemEnablement.dependentItemLinks;
        const dependentItems = this._surveyResponse.itemsWithValues.filter(_ =>
          dependentItemLinks.includes(_.link),
        );
        const isEnabled = item.itemEnablement.isEnabled(dependentItems);
        return isEnabled;
      })
      .map<DisplayQuestionResponseResult>(_ => {
        return {
          type: 'display',
          linkId: _.link,
          label: _.getFriendlyDisplayText(),
          sameLine: _.isOnSameLineAsPreviousItem,
          isFavorite: this._isFavoriteOrParentOfFavorite(
            _.fhirQuestionnaireItem,
          ),
        };
      });
    return displayItems;
  }

  /**
   * Response results from the individual questions and display items sorted by pre-order
   */
  private get _responseResult(): QuestionResponseResult[] {
    const allItems = [
      ...this._valueQuestionResponseResult,
      ...(this._config.excludeDisplayItems
        ? []
        : this._displayQuestionDisplayResult),
    ];
    const questionLinkIds = allItems.map(_ => _.linkId);
    const sortedLineItemIds = this._survey.sortLinkIds(
      questionLinkIds,
      'pre-order',
    );
    return allItems
      .sort((l1, l2) => {
        const l1Index = sortedLineItemIds.findIndex(_ => _ === l1.linkId);
        const l2Index = sortedLineItemIds.findIndex(_ => _ === l2.linkId);
        return l1Index - l2Index;
      })
      .filter(_ => (this._config.excludeNonFavorites ? _.isFavorite : true));
  }

  private _compileSections(
    questionResults: QuestionResponseResult[],
  ): ResponseResult[] {
    const returnValue: ResponseResult[] = [];

    // Adds question to the compiled result.  Recursively adds parent sections
    const addQuestionToReturnValue = (
      questionResponseResult: QuestionResponseResult,
      scopedReturnValue: ResponseResult[],
      ...pathToItem: string[]
    ): void => {
      if (pathToItem.length === 1) {
        scopedReturnValue.push(questionResponseResult);
      } else {
        const [sectionLink, ...restOfPath] = pathToItem;
        let section: SectionResponseResult = scopedReturnValue.find(
          _ => _.linkId === sectionLink,
        ) as SectionResponseResult;
        if (!section) {
          section = {
            linkId: sectionLink,
            type: 'section',
            subResults: [],
            label: this._survey.getSurveyItemWithLinkId(sectionLink).text,
          };
          scopedReturnValue.push(section);
        }
        addQuestionToReturnValue(
          questionResponseResult,
          section.subResults,
          ...restOfPath,
        );
      }
    };

    questionResults.forEach(questionResult => {
      const pathToItem = this._survey.getPathToItem(questionResult.linkId);
      addQuestionToReturnValue(questionResult, returnValue, ...pathToItem);
    });

    const addScoringToItem = (sectionResult: SectionResponseResult): void => {
      const responseItemPath = this._survey.getPathToItem(sectionResult.linkId);
      const correspondingResponseItem =
        this._surveyResponse.getItemAtPath(responseItemPath);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      if (!correspondingResponseItem) return null;

      const scoringResult = this._survey.scoreGroupResponseItem(
        correspondingResponseItem,
      );

      if (!_isNil(scoringResult?.avg)) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        sectionResult.avg = this._getNumberDisplayValue(scoringResult.avg);
      }

      if (!_isNil(scoringResult?.sum)) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        sectionResult.sum = this._getNumberDisplayValue(scoringResult.sum);
      }
    };

    const recursivelyAddScore = (sectionResult: SectionResponseResult) => {
      addScoringToItem(sectionResult);
      (sectionResult.subResults || [])
        .filter(SurveyResponseResults.isSectionResult)
        .forEach(addScoringToItem);
    };

    returnValue
      .filter(SurveyResponseResults.isSectionResult)
      .forEach(recursivelyAddScore);

    return returnValue;
  }

  /**
   * cumulative survey scoring
   */
  private get _surveyScoring(): SurveyTotalResponseResult | null {
    const scoreResult = this._survey.scoreSurvey(this._surveyResponse);

    if (_isNil(scoreResult?.avg) && _isNil(scoreResult?.sum)) return null;

    return {
      type: 'survey',
      label: this._survey.title,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      sum: _isNil(scoreResult?.sum) ? null : String(scoreResult.sum.toFixed(2)),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      avg: _isNil(scoreResult?.avg)
        ? null
        : // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          String(parseFloat(scoreResult.avg.toFixed(4))),
    };
  }

  public get results(): TotalResult {
    const lineItems = this._compileSections(this._responseResult);

    return {
      lineItems,
      surveyTotal: this._surveyScoring,
    };
  }

  public get hasScoring(): boolean {
    return (
      this._survey.isScoringSurvey('sum') ||
      this._survey.isScoringSurvey('avg') ||
      this._survey.surveyContainsScoredSections
    );
  }

  public get author(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._surveyResponse.author;
  }
}

export default SurveyResponseResults;
